import React from "react";

import { defineMessages, useIntl } from "react-intl";

import { Alert } from "@mapmycustomers/ui";

import GoalType from "@app/enum/dashboard/GoalType";
import TargetType from "@app/enum/dashboard/TargetType";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

const messages = defineMessages({
  noGoalMax: {
    id: "dashboard.editModal.configuration.goal.noTeamGoal.max",
    defaultMessage:
      "{isMember, select, true {There is no team max for this chart, so you can set a max for yourself only.} other {There is no team max for this chart, so this max is unique to this user. You can set a team goals for this chart via team goal preferences.}}",
    description:
      "Max Goal Alert in Create/Edit Card modal on the Dashboard page when team goal is not set",
  },
  noGoalMin: {
    id: "dashboard.editModal.configuration.goal.noTeamGoal.min",
    defaultMessage:
      "{isMember, select, true {There is no team goal for this chart, so you can set a goal for yourself only.} other {There is no team goal for this chart, so this goal set below is unique to this user. You can set a team goal for this chart via team goal preferences.}}",
    description:
      "Min Goal Alert in Create/Edit Card modal on the Dashboard page when team goal is not set",
  },
  specificGoalMax: {
    id: "dashboard.editModal.configuration.goal.specificGoal.max",
    defaultMessage:
      "{isMember, select, true {Your manager has set a max for you. You cannot modify this max.} other {Set an individual max below. An individual max cannot be greater than the team max set of {amount} via team goal preferences.}}",
    description:
      "Max Goal Alert in Create/Edit Card modal on the Dashboard page when team goal is set, but allowed to override per user",
  },
  specificGoalMin: {
    id: "dashboard.editModal.configuration.goal.specificGoal.min",
    defaultMessage:
      "{isMember, select, true {Your manager has set a goal for you. You cannot modify this goal.} other {Set an individual goal below. An individual goal cannot be greater than the team goal of {amount} set via team goal preferences.}}",
    description:
      "Min Goal Alert in Create/Edit Card modal on the Dashboard page when team goal is set, but allowed to override per user",
  },
  teamGoalMax: {
    id: "dashboard.editModal.configuration.goal.teamGoal.max",
    defaultMessage:
      "Everyone on this team is expected to collectively stay below the team max of {amount}{hourTime, select, true {h} other {}}. {isMember, select, true {You cannot modify this goal because it is set by your manager.} other {Turn on individual goals for reps via team goal preferences.}}",
    description:
      "Max Goal Alert in Create/Edit Card modal on the Dashboard page when team goal is set",
  },
  teamGoalMin: {
    id: "dashboard.editModal.configuration.goal.teamGoal.min",
    defaultMessage:
      "Each member on this team is expected to contribute equally to the team goal of {amount}{hourTime, select, true {h} other {}}. {isMember, select, true {You cannot modify this goal because it is set by your manager.} other {Turn on individual goals for reps via team goal preferences.}}",
    description:
      "Min Goal Alert in Create/Edit Card modal on the Dashboard page when team goal is set",
  },
});

interface Props {
  goalConfiguration?: GoalConfiguration;
  goalFormat: "amount" | "time";
  goalType: GoalType;
  isMember?: boolean;
  scope: Scope;
  teamGoal?: GoalConfiguration;
}

const GoalAlert: React.FC<Props> = ({
  goalConfiguration,
  goalFormat,
  goalType,
  isMember,
  scope,
  teamGoal,
}) => {
  const intl = useIntl();

  // we only show this alert for
  if (!scope.teamId || !scope.userId) {
    return null;
  }

  const { target = 0, type: targetType = TargetType.TEAM } = teamGoal ?? goalConfiguration ?? {};

  // We have 12 message here:
  //
  // 1. min goal, manager, team goal set
  // 2. min goal, manager, team goal set, target_type = individual
  // 3. min goal, manager, no team goal set
  //
  // 4. max goal, manager, team goal set
  // 5. max goal, manager, team goal set, target_type = individual
  // 6. max goal, manager, no team goal set
  //
  // 7. min goal, rep, team goal set
  // 8. min goal, rep, team goal set, target_type = individual
  // 9. min goal, rep, no team goal set
  //
  // 10. max goal, rep, team goal set
  // 11. max goal, rep, team goal set, target_type = individual
  // 12. max goal, rep, no team goal set
  //
  // In our messages var we have 6 messages (each has text for manager and member cases):
  // 3 messages for min goal, 3 messages for max goal
  // And in each group we have a message for the case when there's no team goal, message
  // when there's a total team goal and a message for team goal, but with specific
  // targets per user.
  const message =
    goalType === GoalType.MIN_GOAL
      ? teamGoal?.enabled
        ? intl.formatMessage(
            targetType === TargetType.INDIVIDUAL ? messages.specificGoalMin : messages.teamGoalMin,
            { amount: target, hourTime: goalFormat === "time", isMember }
          )
        : intl.formatMessage(messages.noGoalMin, { isMember })
      : teamGoal?.enabled
      ? intl.formatMessage(
          targetType === TargetType.INDIVIDUAL ? messages.specificGoalMax : messages.teamGoalMax,
          { amount: target, isMember }
        )
      : intl.formatMessage(messages.noGoalMax, { isMember });

  return <Alert message={message} showIcon type="info" />;
};

export default GoalAlert;
